import { ref } from "vue";
import { showAlert } from "@allwyn/design-system/src/modal-alert/show-alert";
import { useNgSession } from "@allwyn/ng/ng-session";
import { useSessionStorage } from "@allwyn/storage/composables/use-storage";

const PENDING_DEBUG_EVENTS_KEY = "webview-debug-pending-events";
const DEBUG_MODE_ACTIVE_KEY = "webview-debug-mode-active";
const WEBVIEW_DEBUG_LAST_INIT_URL_KEY = "webview-debug-last-init-url";
const webViewDebugModeOn = ref(false);

let lastEventKey: string | null = null;
let lastEventTime: number = 0;

export default function useWebviewDebugMode() {
    const storage = useSessionStorage();

    const showPendingEvents = (): void => {
        try {
            const pending: string[] = JSON.parse(storage.getItem(PENDING_DEBUG_EVENTS_KEY) || "[]");
            if (pending.length === 0) return;
            storage.removeItem(PENDING_DEBUG_EVENTS_KEY);
            showAlert({
                title: "WebView Debug Events (před přesměrováním)",
                subText: pending.join("<br/>"),
            });
        } catch {
            // ignore catch
        }
    };

    const initWebviewDebugMode = (): void => {
        try {
            if (environment.env == "Production") return;

            // Restore debug mode state that was active before a redirect
            webViewDebugModeOn.value = storage.getItem(DEBUG_MODE_ACTIVE_KEY) === "true";

            // Create debug button
            const debugBtn = document.createElement("button");
            debugBtn.className = "webview-debug";
            debugBtn.attributes["type"] = "button";
            debugBtn.onclick = () => {
                webViewDebugModeOn.value = !webViewDebugModeOn.value;
                storage.setItem(DEBUG_MODE_ACTIVE_KEY, String(webViewDebugModeOn.value), "essential");
                document.querySelector(".webview-debug__mode")?.classList.toggle("webview-debug__mode--active");
                if (webViewDebugModeOn.value) {
                    const { session } = useNgSession();

                    showAlert({
                        title: "WebView Debug Mode",
                        subText: `Aplikace: ${environment.appName}<br/>
                        URL: ${window.location.href}<br/>
                        PlayerId z BE: ${window.player?.id || "---"}<br/>
                        PlayerId od NG: ${session.value?.PlayerID || "---"}<br/>
                        SessionToken z BE: ${window.player?.sessionToken || "---"}<br/>
                        SessionToken od NG: ${session.value?.SessionToken || "---"}<br/>`,
                    });
                    showPendingEvents();
                }
            };

            debugBtn.insertAdjacentHTML(
                "beforeend",
                '<span>WebView Debug</span> <span class="webview-debug__mode">Active</span>'
            );
            document.querySelector("body").appendChild(debugBtn);

            // Auto-replay events captured before a redirect on iOS (no user interaction needed)
            if (webViewDebugModeOn.value) {
                document.querySelector(".webview-debug__mode")?.classList.add("webview-debug__mode--active");

                // Only show pending events if we actually navigated to a different page
                const lastInitUrl = storage.getItem(WEBVIEW_DEBUG_LAST_INIT_URL_KEY);
                const currentUrl = window.location.href;

                if (lastInitUrl && lastInitUrl !== currentUrl) {
                    // We redirected — show pending events (iOS case)
                    showPendingEvents();
                }

                storage.setItem(WEBVIEW_DEBUG_LAST_INIT_URL_KEY, currentUrl, "essential");
            }
        } catch (e) {
            console.error(`WebView Debug Mode Initialiazation Failed: ${e}`);
        }
    };

    const showEventAlert = (interfaceName: string, methodName: string, argument: string): void => {
        try {
            if (!webViewDebugModeOn.value) return;

            const eventMessage =
                argument !== null && argument !== undefined
                    ? `Interface ${interfaceName}; method ${methodName}('${argument}')`
                    : `Interface ${interfaceName}; method ${methodName}()`;

            // Deduplicate: skip if same method+argument logged within last 50ms.
            // @see file {mobile-app-integration.ts} (TODO block that calls triggerMobileAppEvent recursively)
            // iOS executes all recursive calls before redirect; Android redirects immediately.
            // Deduping ensures consistent debug logs across both platforms.
            const currentEventKey = `${methodName}:${argument ?? ""}`;
            const now = performance.now();
            if (lastEventKey === currentEventKey && now - lastEventTime < 50) {
                return;
            }
            lastEventKey = currentEventKey;
            lastEventTime = now;

            // Queue event to storage — survives iOS redirect and replays on next page load
            const pending: string[] = JSON.parse(storage.getItem(PENDING_DEBUG_EVENTS_KEY) || "[]");
            pending.push(eventMessage);
            storage.setItem(PENDING_DEBUG_EVENTS_KEY, JSON.stringify(pending), "essential");

            confirm(eventMessage);
        } catch (e) {
            console.error(`Show Alert For WebView Debug Mode Failed: ${e}`);
        }
    };

    return {
        showEventAlert,
        initWebviewDebugMode,
        webViewDebugModeOn,
    };
}
